import { Link } from '@inertiajs/react';

import Button from '@/components/public/ui/Button';
import Section from '@/components/public/ui/Section';
import SectionHeader from '@/components/public/ui/SectionHeader';
import { trackUmamiEvent } from '@/lib/analytics';

type NewsItem = {
  id: number;
  title: string;
  slug: string;
  excerpt: string | null;
  date: string | null;
  imageUrl: string | null;
};

type Props = {
  news: NewsItem[];
};

function formatNewsDate(value: string | null): string | null {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString('pt-PT', { day: 'numeric', month: 'long', year: 'numeric' });
}

function NewsTile({ item, featured = false }: { item: NewsItem; featured?: boolean }) {
  const date = formatNewsDate(item.date);
  return (
    <Link
      href={`/news/${item.slug}`}
      onClick={() => trackUmamiEvent('news_open', { slug: item.slug, source: 'home_notice' })}
      className="group flex flex-col"
    >
      <div
        className={`mb-4 overflow-hidden rounded-lg border border-white/10 ${
          featured ? 'aspect-[16/11]' : 'aspect-[16/10]'
        }`}
      >
        {item.imageUrl ? (
          <img
            src={item.imageUrl}
            alt={item.title}
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-white/5">
            <span className="font-headline text-4xl font-bold text-white/20">CP</span>
          </div>
        )}
      </div>
      <span className="text-gold-leaf text-xs font-bold tracking-[0.08em] uppercase">Destaque</span>
      <h3
        className={`font-headline mt-2 font-semibold text-[#fbf8f1] ${featured ? 'text-2xl' : 'text-base'}`}
      >
        {item.title}
      </h3>
      {date && <time className="mt-1 text-[13px] text-[#a89e8b]">{date}</time>}
      {featured && item.excerpt && <p className="mt-2 text-sm text-[#c2b9a6]">{item.excerpt}</p>}
    </Link>
  );
}

const NoticeCarousel = ({ news }: Props) => {
  if (!news || news.length === 0) return null;

  const hasFeatureLayout = news.length >= 3;

  return (
    <Section tone="ink" spacing="md">
      <SectionHeader
        onInk
        eyebrow="Atualidade"
        title="Avisos e notícias"
        action={
          <Button
            variant="white"
            size="sm"
            href="/news"
            iconAfter="arrow_forward"
            onClick={() =>
              trackUmamiEvent('home_section_cta_click', {
                section: 'news',
                label: 'Todas as notícias',
                target: '/news',
              })
            }
          >
            Todas as notícias
          </Button>
        }
      />

      {hasFeatureLayout ? (
        // 3+ items: large featured + two compact (the validated example layout).
        <div className="grid grid-cols-1 gap-7 lg:grid-cols-[1.4fr_1fr_1fr]">
          <NewsTile item={news[0]} featured />
          <NewsTile item={news[1]} />
          <NewsTile item={news[2]} />
        </div>
      ) : (
        // 1–2 items: balanced equal tiles (no oversized featured).
        <div
          className={`grid grid-cols-1 gap-7 ${news.length === 2 ? 'sm:grid-cols-2' : 'max-w-md'}`}
        >
          {news.slice(0, 2).map((item) => (
            <NewsTile key={item.slug} item={item} />
          ))}
        </div>
      )}
    </Section>
  );
};

export default NoticeCarousel;
